// Modelo Excel de participantes que o cliente descarrega no link da ação, preenche e volta a carregar.
// Serve para a lista de formandos (antes da sessão) e para as presenças (depois): a coluna «Presente» é opcional.
// O ficheiro é validado linha a linha antes de seguir, para não chegarem dados com erros.
import { normalizarData } from './certificados';

const COLS_BASE = [['nome', 'Nome completo']];
const COLS_OFICIAL = [['naturalidade', 'Naturalidade (concelho)'], ['nascimento', 'Data de nascimento (dd/mm/aaaa)'],
  ['documento', 'N.º do cartão de cidadão'], ['validade', 'Validade do cartão (dd/mm/aaaa)']];
const COL_PRESENTE = ['presente', 'Presente (Sim/Não)'];
export const colunasModelo = (oficial) => [...COLS_BASE, ...(oficial ? COLS_OFICIAL : []), COL_PRESENTE];

const limpa = (s) => String(s == null ? '' : s).normalize('NFKD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
// reconhece as colunas pelo nome, mesmo que o cliente mude a ordem ou escreva de outra maneira
const QUAL = [
  ['nome', /^(nome|nome completo|formando|participante|colaborador)/],
  ['naturalidade', /^(naturalidade|natural|concelho)/],
  ['nascimento', /(nascimento|data nasc)/],
  ['documento', /(cartao|cc\b|bi\b|identificacao|documento)/],
  ['validade', /(validade|valido)/],
  ['presente', /^(presente|presenca|esteve)/],
];

export async function descarregarModelo(a, oficial) {
  const XLSX = await import('xlsx');
  const cols = colunasModelo(oficial);
  const ws = XLSX.utils.aoa_to_sheet([cols.map(([, t]) => t)]);
  ws['!cols'] = cols.map(([k]) => ({ wch: k === 'nome' ? 38 : 26 }));
  // colunas de texto, para o Excel não transformar datas e números do CC
  const range = XLSX.utils.decode_range('A1:F500');
  for (let r = 1; r <= range.e.r; r++) for (let c = 0; c < cols.length; c++) {
    const ref = XLSX.utils.encode_cell({ r, c }); ws[ref] = { t: 's', v: '', z: '@' };
  }
  ws['!ref'] = XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: 500, c: cols.length - 1 } });
  const info = XLSX.utils.aoa_to_sheet([
    ['Como preencher'],
    ['Uma pessoa por linha, a partir da linha 2 da folha «Participantes». Não mude os títulos das colunas.'],
    ...(oficial ? [['Formação certificada: o certificado de formação profissional exige nome completo, naturalidade, data de nascimento e número e validade do cartão de cidadão. Datas no formato dd/mm/aaaa.'],
      ['Estes dados servem apenas para os certificados e a documentação da ação.']] : []),
    ['«Presente»: preencha depois da sessão (Sim ou Não). Antes da sessão pode ficar em branco.'],
    ['Guarde e carregue o ficheiro no mesmo link onde o descarregou.'],
  ]);
  info['!cols'] = [{ wch: 120 }];
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Participantes');
  XLSX.utils.book_append_sheet(wb, info, 'Instruções');
  const nome = String(a.titulo || 'acao').normalize('NFKD').replace(/[̀-ͯ]/g, '').replace(/[^\w]+/g, '_').slice(0, 40);
  XLSX.writeFile(wb, `Participantes_${nome}.xlsx`);
}

const dataOk = (s) => { const m = String(s || '').match(/^(\d{2})\/(\d{2})\/(\d{4})$/); if (!m) return false; const d = new Date(+m[3], +m[2] - 1, +m[1]); return d.getDate() === +m[1] && d.getMonth() === +m[2] - 1; };
const simNao = (s) => { const t = limpa(s); if (!t) return undefined; if (/^(s|sim|yes|y|x|1|presente|true)$/.test(t)) return true; if (/^(n|nao|no|0|ausente|falta|false)$/.test(t)) return false; return null; };

// lê o Excel/CSV preenchido. Devolve { lista, erros } — erros: [{ linha, nome, texto }]
export async function lerFicheiroParticipantes(file, oficial) {
  const XLSX = await import('xlsx');
  const wb = XLSX.read(await file.arrayBuffer(), { cellDates: true });
  const folha = wb.Sheets['Participantes'] || wb.Sheets[wb.SheetNames[0]];
  const rows = XLSX.utils.sheet_to_json(folha, { header: 1, raw: false, dateNF: 'dd/mm/yyyy', defval: '' });
  const iCab = rows.findIndex(r => r.some(c => /^nome/.test(limpa(c))));
  if (iCab < 0) return { lista: [], erros: [{ linha: 1, nome: '', texto: 'Não encontrei a coluna «Nome completo». Use o modelo descarregado neste link.' }] };
  const mapa = {};
  rows[iCab].forEach((c, i) => { const t = limpa(c); const q = QUAL.find(([k, re]) => !(k in mapa) && re.test(t)); if (q) mapa[q[0]] = i; });
  const lista = [], erros = [];
  rows.slice(iCab + 1).forEach((r, j) => {
    const linha = iCab + j + 2;
    const v = (k) => (mapa[k] != null ? String(r[mapa[k]] ?? '').trim() : '');
    const nome = v('nome').replace(/\s+/g, ' ');
    if (!nome && !r.some(c => String(c).trim())) return;            // linha vazia
    const p = { nome };
    const falta = [];
    if (!nome) falta.push('nome');
    if (oficial) {
      p.naturalidade = v('naturalidade'); p.nascimento = normalizarData(v('nascimento')); p.documento = v('documento').replace(/\s+/g, ' '); p.validade = normalizarData(v('validade'));
      if (!p.naturalidade) falta.push('naturalidade');
      if (!p.nascimento) falta.push('data de nascimento'); else if (!dataOk(p.nascimento)) falta.push(`data de nascimento inválida («${p.nascimento}»)`);
      if (!p.documento) falta.push('n.º do CC'); else if (!/^[0-9A-Za-z ]{6,15}$/.test(p.documento)) falta.push(`n.º do CC estranho («${p.documento}»)`);
      if (!p.validade) falta.push('validade do CC'); else if (!dataOk(p.validade)) falta.push(`validade inválida («${p.validade}»)`);
    }
    const pr = simNao(v('presente'));
    if (pr === null) falta.push(`«Presente» deve ser Sim ou Não («${v('presente')}»)`);
    else if (pr !== undefined) p.presente = pr;
    if (falta.length) erros.push({ linha, nome, texto: falta.join(', ') });
    lista.push(p);
  });
  if (!lista.length && !erros.length) erros.push({ linha: iCab + 2, nome: '', texto: 'O ficheiro não tem nenhuma pessoa preenchida.' });
  return { lista, erros };
}
